import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';

import type { ScriptElement } from '@/lib/schema.types';
import { useEditorActions } from '@/store/editor.store';

interface Props {
  element: ScriptElement;
  slideId: string;
  rowId: string;
  columnId: string;
  isSelected: boolean;
}

export function TextElement({ element, slideId, rowId, columnId, isSelected }: Props) {
  const actions = useEditorActions();
  const initial = typeof element.content === 'string' ? element.content : 'Tap to edit';

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: 'Type something…' }),
    ],
    content: initial.startsWith('<') ? initial : `<p>${escapeHtml(initial)}</p>`,
    immediatelyRender: false,
    editable: isSelected,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      actions.updateElement(slideId, rowId, columnId, element.id, { content: html });
    },
  });

  useEffect(() => {
    editor?.setEditable(isSelected);
  }, [editor, isSelected]);

  const style: React.CSSProperties = {
    fontSize: (element.style?.fontSize as number | undefined) ?? 18,
    fontWeight: (element.style?.fontWeight as number | undefined) ?? 400,
    color: (element.style?.color as string | undefined) ?? '#1a1a2e',
    fontFamily: (element.style?.fontFamily as string | undefined) ?? undefined,
    textAlign: (element.style?.textAlign as React.CSSProperties['textAlign']) ?? 'left',
    lineHeight: 1.4,
  };

  return (
    <div style={style} className="w-full">
      <EditorContent editor={editor} />
    </div>
  );
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
